import { IError } from '@axmit/error-helper';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { ERoutesPrivate } from 'common/models/routesModel';
import { AppDispatch, history, RootState } from 'app/store';
import { getCustomerModel } from 'app/store/reducers/customer.reducer';
import { getCartModel, setCartModel } from 'app/store/reducers/cart.reducer';
import { getMenuAddOns } from 'app/store/reducers/menu.reducer';
import { getBitecoinCustomerInfo } from 'app/store/reducers/bitecoin.reducer';
import { EAuthErrorCode, EAuthErrorMessage } from 'entities/Auth/Auth.models';
import { ECartErrorCode, ECartErrorMessage } from 'entities/Cart/Cart.models';
import { isUnavailableError } from 'entities/Orders/Orders.helpers';
import {
  EOrdersErrorCode,
  EOrdersErrorMessage,
  EOrderStatus,
  IAddOrderParams,
  IGetLastOrderParams,
  IOrderModel,
  IRejectOrderParams,
} from 'entities/Orders/Orders.models';
import { orderTransport } from 'entities/Orders/Orders.transport';
import { EStoreErrorCode, EStoreErrorMessage } from 'entities/Store/Store.models';

export interface IState {
  // order model
  orderModel: IOrderModel | null;
  orderModelLoading: boolean;
  orderModelError: IError | null;
  // last order model
  lastOrderModel: IOrderModel | null;
  lastOrderModelLoading: boolean;
  lastOrderModelError: IError | null;
}

const initialState: IState = {
  // order model
  orderModel: null,
  orderModelLoading: false,
  orderModelError: null,
  // last order model
  lastOrderModel: null,
  lastOrderModelLoading: false,
  lastOrderModelError: null,
};

export const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    // order model
    setOrderModelLoading(state, action: PayloadAction<boolean>) {
      state.orderModelLoading = action.payload;
    },
    setOrderModel(state, action: PayloadAction<IOrderModel | null>) {
      state.orderModel = action.payload;
    },
    setOrderModelError(state, action: PayloadAction<IError | null>) {
      state.orderModelError = action.payload;
    },
    // last order model
    setLastOrderModelLoading(state, action: PayloadAction<boolean>) {
      state.lastOrderModelLoading = action.payload;
    },
    setLastOrderModel(state, action: PayloadAction<IOrderModel | null>) {
      state.lastOrderModel = action.payload;
    },
    setLastOrderModelError(state, action: PayloadAction<IError | null>) {
      state.lastOrderModelError = action.payload;
    },
  },
});

export const {
  // order model
  setOrderModelLoading,
  setOrderModel,
  setOrderModelError,
  // last order model
  setLastOrderModelLoading,
  setLastOrderModel,
  setLastOrderModelError,
} = orderSlice.actions;
export default orderSlice.reducer;

export const addOrder = (params: IAddOrderParams) => {
  async function thunk(dispatch: AppDispatch, getState: () => RootState) {
    dispatch(setOrderModelLoading(true));

    const store = getState();

    try {
      const orderModel = await orderTransport.postOrder(params);

      dispatch(setOrderModel(orderModel));

      const { checkoutUrl, status } = orderModel;

      if (checkoutUrl && status === EOrderStatus.PaymentAwaiting) {
        window.location.replace(checkoutUrl);

        return;
      }

      if (status === EOrderStatus.New) {
        history.push(ERoutesPrivate.Order);
        dispatch(getCartModel());
        dispatch(getBitecoinCustomerInfo());
      }
    } catch (error) {
      const _error = error as IError;
      dispatch(setOrderModelError(_error));

      if (_error?.data?.code === EOrdersErrorCode.ActiveOrderExists) {
        message.error(EOrdersErrorMessage.ActiveOrderExists);
        try {
          const currentOrder = await orderTransport.getCurrentOrder();
          dispatch(setOrderModel(currentOrder));

          if (currentOrder.status === EOrderStatus.PaymentAwaiting) {
            message.info(EOrdersErrorMessage.PaymentProcessing);
          } else if (currentOrder.status === EOrderStatus.New) {
            dispatch(setCartModel(null));
            history.push(ERoutesPrivate.Order);
          } else {
            message.info(EOrdersErrorMessage.ActiveOrderExists);
          }
        } catch {
          message.error(EOrdersErrorMessage.ActiveOrderExists);
        }
      }
      if (_error?.data?.code === EAuthErrorCode.UserBlocked) {
        message.error(EAuthErrorMessage.UserBlocked);
      }
      if (_error?.data?.code === EStoreErrorCode.StoreIsClosed) {
        message.error(EStoreErrorMessage.StoreIsClosed);
        const userId = store.auth.authModel?.access?.userId;

        if (userId) {
          dispatch(getCustomerModel(userId));
        }
      }

      const hasUnavailableError = isUnavailableError(_error);

      if (hasUnavailableError) {
        dispatch(getCartModel());
        const storeId = store?.customer?.customerModel?.store?.id;

        if (storeId) {
          dispatch(getMenuAddOns(storeId));
        }
      }

      if (_error?.data?.code === ECartErrorCode.PromoNotFound) {
        message.error(ECartErrorMessage.PromoNotFound);
      }

      if (_error?.data?.code === EStoreErrorCode.NotFound) {
        message.error(EStoreErrorMessage.NotFound);
      }
      if (_error?.data?.code === EOrdersErrorCode.EmptyItems) {
        message.error(EOrdersErrorMessage.EmptyItems);
      }

      const handledCodes: string[] = [
        EOrdersErrorCode.ActiveOrderExists,
        EAuthErrorCode.UserBlocked,
        EStoreErrorCode.StoreIsClosed,
        EOrdersErrorCode.UnavailableItemInOrder,
        ECartErrorCode.PromoNotFound,
        EStoreErrorCode.NotFound,
        EOrdersErrorCode.EmptyItems,
      ];

      if (!handledCodes.includes(_error?.data?.code) && _error?.data?.message) {
        message.error(_error.data.message);
      }
    } finally {
      dispatch(setOrderModelLoading(false));
    }
  }

  return thunk;
};

export const rejectOrder = (params: IRejectOrderParams, failCb: () => void) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setOrderModelLoading(true));

    try {
      const orderModel = await orderTransport.rejectOrder(params);

      dispatch(setOrderModel(orderModel));
    } catch (error) {
      const _error = error as IError;

      dispatch(setOrderModelError(_error));

      if (_error.data.code === EOrdersErrorCode.AlreadyFinished) {
        message.error(EOrdersErrorMessage.AlreadyFinished);

        if (failCb) {
          failCb();
        }
      }
    } finally {
      dispatch(setOrderModelLoading(false));
    }
  }

  return thunk;
};

export const getCurrentOrder = () => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setOrderModelLoading(true));
    dispatch(setOrderModelError(null));

    try {
      const orderModel = await orderTransport.getCurrentOrder();

      dispatch(setOrderModel(orderModel));

      if (orderModel.status === EOrderStatus.Finished) {
        dispatch(getBitecoinCustomerInfo());
      }
    } catch (error) {
      const _error = error as IError;

      dispatch(setOrderModelError(_error));
    } finally {
      dispatch(setOrderModelLoading(false));
    }
  }

  return thunk;
};

export const getLastOrder = (params: IGetLastOrderParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setLastOrderModelLoading(true));
    dispatch(setLastOrderModelError(null));

    try {
      const lastOrderModel = await orderTransport.getLastOrder(params);

      dispatch(setLastOrderModel(lastOrderModel));
    } catch (error) {
      const _error = error as IError;

      dispatch(setLastOrderModelError(_error));
      dispatch(setLastOrderModel(null));
    } finally {
      dispatch(setLastOrderModelLoading(false));
    }
  }

  return thunk;
};
